import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "../scripts/AuthContext";
import { getFullName } from "@/lib/userUtils";
import OrgMemberAvatar from "@/components/avatars/OrgMemberAvatar";
import { API_BASE_URL } from "@/lib/apiBase";
import { toastSuccess, getErrorMessage } from "@/lib/toast";
import {
  INVALID_EMAIL_MESSAGE,
  partitionInviteEmails,
} from "@/lib/inviteEmails";
import { cn } from "@/lib/utils";

const InviteUserDialog = ({ isOpen, onClose, orgId, teamId, onSuccess }) => {
  const { accessToken, login, logout } = useAuth();
  const [email, setEmail] = useState("");
  const [error, setError] = useState(null);
  const [failedRows, setFailedRows] = useState([]);
  const [isInviting, setIsInviting] = useState(false);
  const isTeamInvite = teamId !== undefined && teamId !== null;

  const [memberQuery, setMemberQuery] = useState("");
  const [orgMembers, setOrgMembers] = useState([]);
  const [teamMemberIds, setTeamMemberIds] = useState(() => new Set());
  const [membersLoading, setMembersLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [inviteTab, setInviteTab] = useState("email");
  const [directoryEnabled, setDirectoryEnabled] = useState(false);
  const [directoryHint, setDirectoryHint] = useState("");
  const [dirGroups, setDirGroups] = useState([]);
  const [dirPeople, setDirPeople] = useState([]);
  const [dirProvider, setDirProvider] = useState("");
  const [dirLoading, setDirLoading] = useState(false);
  const [dirLoadingMore, setDirLoadingMore] = useState(false);
  const [dirSelectingGroup, setDirSelectingGroup] = useState(false);
  const [dirQuery, setDirQuery] = useState("");
  const [dirGroupQuery, setDirGroupQuery] = useState("");
  const [dirGroup, setDirGroup] = useState("");
  const [selectedSubs, setSelectedSubs] = useState(() => new Set());
  const [dirPage, setDirPage] = useState({
    offset: 0,
    limit: 200,
    hasMore: false,
  });

  const makeAuthenticatedRequest = async (requestFn, token = accessToken) => {
    try {
      return await requestFn(token);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        if (err.response.data.message === "Token has expired") {
          try {
            const refreshResponse = await axios.post(
              `${API_BASE_URL}/auth/refresh`,
              null,
              { withCredentials: true }
            );
            const newAccessToken =
              refreshResponse.data.access_token ||
              refreshResponse.data.accessToken;
            login(newAccessToken);
            return await requestFn(newAccessToken);
          } catch (refreshError) {
            console.error("Failed to refresh access token:", refreshError);
            logout();
            throw new Error("Session expired. Please log in again.");
          }
        }
      }
      throw err;
    }
  };

  useEffect(() => {
    if (!isOpen || !isTeamInvite || !orgId || !accessToken) return;
    let cancelled = false;
    (async () => {
      setMembersLoading(true);
      setError(null);
      try {
        const [orgRes, teamRes] = await Promise.all([
          makeAuthenticatedRequest(async (token) =>
            axios.get(`${API_BASE_URL}/org/${orgId}/members`, {
              headers: { Authorization: `Bearer ${token}` },
            })
          ),
          makeAuthenticatedRequest(async (token) =>
            axios.get(`${API_BASE_URL}/team/${teamId}/members`, {
              headers: { Authorization: `Bearer ${token}` },
              params: { org_id: orgId },
            })
          ),
        ]);
        if (cancelled) return;
        setOrgMembers(orgRes.data || []);
        const ids = new Set(
          (teamRes.data || [])
            .map((row) => row.user?.user_id || row.user_id)
            .filter(Boolean)
        );
        setTeamMemberIds(ids);
      } catch (err) {
        console.error("Failed to load members for team invite:", err);
        if (!cancelled) {
          setError(
            err.response?.data?.message ||
              err.message ||
              "Failed to load organization members"
          );
        }
      } finally {
        if (!cancelled) setMembersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh when dialog opens
  }, [isOpen, isTeamInvite, orgId, teamId, accessToken]);

  useEffect(() => {
    if (!isOpen || isTeamInvite || !orgId || !accessToken) {
      setDirectoryEnabled(false);
      setDirectoryHint("");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [statusRes, capRes] = await Promise.all([
          makeAuthenticatedRequest(async (token) =>
            axios.get(
              `${API_BASE_URL}/org/${encodeURIComponent(orgId)}/sso/status`,
              { headers: { Authorization: `Bearer ${token}` } }
            )
          ),
          makeAuthenticatedRequest(async (token) =>
            axios.get(
              `${API_BASE_URL}/org/${encodeURIComponent(orgId)}/sso/directory/capability`,
              { headers: { Authorization: `Bearer ${token}` } }
            )
          ),
        ]);
        if (cancelled) return;
        const status = statusRes.data || {};
        const cap = capRes.data || {};
        const showDirectory =
          Boolean(status.entitled && status.has_config) &&
          Boolean(cap.directory_supported) &&
          Boolean(cap.directory_ready);
        setDirectoryEnabled(showDirectory);
        setDirectoryHint(
          showDirectory
            ? ""
            : cap.directory_ready === false &&
                cap.directory_supported &&
                status.has_config
              ? "Add directory import credentials (App 2) in Settings → Security."
              : String(
                  cap.reason ||
                    "Directory import is not available for this identity provider yet."
                )
        );
      } catch {
        if (!cancelled) {
          setDirectoryEnabled(false);
          setDirectoryHint("Directory import could not be checked.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isTeamInvite, orgId, accessToken]);

  const fetchDirectoryPeople = async ({
    token,
    group = "",
    q = "",
    offset = 0,
    limit = 200,
  }) => {
    const usersRes = await axios.get(
      `${API_BASE_URL}/org/${encodeURIComponent(orgId)}/sso/directory/users`,
      {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          group: group || undefined,
          q: q || undefined,
          offset,
          limit,
        },
      }
    );
    const people = usersRes.data?.people || [];
    const page = usersRes.data?.page || {};
    return {
      provider: usersRes.data?.provider || "",
      people,
      page: {
        offset: Number(page.offset || offset),
        limit: Number(page.limit || limit),
        hasMore: Boolean(page.has_more),
      },
    };
  };

  useEffect(() => {
    if (
      !isOpen ||
      isTeamInvite ||
      !directoryEnabled ||
      inviteTab !== "directory" ||
      !orgId ||
      !accessToken
    ) {
      return;
    }
    let cancelled = false;
    (async () => {
      setDirLoading(true);
      try {
        const [groupsRes, usersRes] = await Promise.all([
          makeAuthenticatedRequest(async (token) =>
            axios.get(
              `${API_BASE_URL}/org/${encodeURIComponent(orgId)}/sso/directory/groups`,
              {
                headers: { Authorization: `Bearer ${token}` },
                params: { q: dirGroupQuery || undefined },
              }
            )
          ),
          makeAuthenticatedRequest(async (token) =>
            fetchDirectoryPeople({
              token,
              group: dirGroup,
              q: dirQuery,
              offset: 0,
              limit: 200,
            })
          ),
        ]);
        if (cancelled) return;
        setDirGroups(groupsRes.data?.groups || []);
        setDirProvider(groupsRes.data?.provider || usersRes.provider || "");
        const people = usersRes.people || [];
        setDirPeople(people);
        setDirPage(usersRes.page);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError(
            getErrorMessage(err, "Could not load the identity directory.")
          );
        }
      } finally {
        if (!cancelled) setDirLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    isTeamInvite,
    directoryEnabled,
    inviteTab,
    orgId,
    accessToken,
    dirGroup,
    dirGroupQuery,
    dirQuery,
  ]);

  const inviteCandidates = useMemo(() => {
    return (orgMembers || [])
      .map((m) => ({
        user: {
          ...(m.user || m),
          org_email: m.org_email ?? (m.user || m).org_email ?? null,
        },
        title: m.title,
        org_email: m.org_email ?? null,
      }))
      .filter(({ user: u }) => u?.user_id)
      .filter(({ user: u }) => !teamMemberIds.has(u.user_id));
  }, [orgMembers, teamMemberIds]);

  const filteredCandidates = useMemo(() => {
    const q = memberQuery.trim().toLowerCase();
    if (!q) return inviteCandidates;
    return inviteCandidates.filter(({ user: u, title, org_email }) => {
      const haystack = [
        u.first_name,
        u.last_name,
        getFullName(u),
        org_email || u.org_email,
        title,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [inviteCandidates, memberQuery]);

  const visibleDirPeople = useMemo(() => dirPeople, [dirPeople]);

  const loadMoreDirectoryPeople = async () => {
    if (!dirPage.hasMore || dirLoadingMore || !orgId || !accessToken) return;
    setDirLoadingMore(true);
    try {
      const res = await makeAuthenticatedRequest(async (token) =>
        fetchDirectoryPeople({
          token,
          group: dirGroup,
          q: dirQuery,
          offset: dirPage.offset + dirPage.limit,
          limit: dirPage.limit,
        })
      );
      const seen = new Set(dirPeople.map((p) => p.idp_subject));
      const merged = [...dirPeople];
      for (const p of res.people || []) {
        if (!seen.has(p.idp_subject)) {
          seen.add(p.idp_subject);
          merged.push(p);
        }
      }
      setDirPeople(merged);
      setDirPage(res.page);
    } catch (err) {
      setError(getErrorMessage(err, "Could not load more directory people."));
    } finally {
      setDirLoadingMore(false);
    }
  };

  const selectWholeGroup = async (groupId) => {
    setDirGroup(groupId);
    setDirSelectingGroup(true);
    try {
      let offset = 0;
      const limit = 200;
      let keepGoing = true;
      const all = [];
      const fetchPage = (pageOffset) =>
        makeAuthenticatedRequest(async (token) =>
          fetchDirectoryPeople({
            token,
            group: groupId,
            q: dirQuery,
            offset: pageOffset,
            limit,
          })
        );
      while (keepGoing) {
        // eslint-disable-next-line no-await-in-loop
        const res = await fetchPage(offset);
        all.push(...(res.people || []));
        keepGoing = Boolean(res.page?.hasMore);
        offset = Number(res.page?.offset || offset) + Number(res.page?.limit || limit);
      }
      const uniq = [];
      const seen = new Set();
      for (const p of all) {
        if (seen.has(p.idp_subject)) continue;
        seen.add(p.idp_subject);
        uniq.push(p);
      }
      setDirPeople(uniq);
      setDirPage({ offset: 0, limit, hasMore: false });
      setSelectedSubs((prev) => {
        const next = new Set(prev);
        uniq.forEach((p) => next.add(p.idp_subject));
        return next;
      });
    } catch (err) {
      setError(getErrorMessage(err, "Could not load group members."));
    } finally {
      setDirSelectingGroup(false);
    }
  };

  const toggleDirPerson = (sub) => {
    setSelectedSubs((prev) => {
      const next = new Set(prev);
      if (next.has(sub)) next.delete(sub);
      else next.add(sub);
      return next;
    });
  };

  const resetForm = () => {
    setEmail("");
    setMemberQuery("");
    setSelectedUserId(null);
    setError(null);
    setFailedRows([]);
    setInviteTab("email");
    setDirQuery("");
    setDirGroupQuery("");
    setDirGroup("");
    setSelectedSubs(new Set());
    setDirPeople([]);
    setDirPage({ offset: 0, limit: 200, hasMore: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setFailedRows([]);

    if (isTeamInvite) {
      if (!selectedUserId) {
        setError("Select a member to invite");
        return;
      }
    } else if (inviteTab === "directory") {
      if (selectedSubs.size === 0) {
        setError("Select at least one directory person");
        return;
      }
    } else {
      const { parsed, valid } = partitionInviteEmails(email);
      if (parsed.length === 0) {
        setError("Add at least one work email");
        return;
      }
      if (valid.length === 0) {
        setFailedRows(
          parsed.map((address) => ({
            email: address,
            message: INVALID_EMAIL_MESSAGE,
          }))
        );
        setError(INVALID_EMAIL_MESSAGE);
        return;
      }
    }

    setIsInviting(true);

    try {
      const requestBody = {
        org_id: orgId,
        role: "member",
      };

      if (isTeamInvite) {
        requestBody.team_id = teamId;
        requestBody.user_id = selectedUserId;
        await makeAuthenticatedRequest(async (token) => {
          const response = await axios.post(
            `${API_BASE_URL}/invitations`,
            requestBody,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          return response.data;
        });
        resetForm();
        toastSuccess("Invite sent");
        if (onSuccess) onSuccess();
        onClose();
      } else if (inviteTab === "directory") {
        const people = dirPeople
          .filter((p) => selectedSubs.has(p.idp_subject))
          .map((p) => ({
            email: p.email,
            idp_subject: p.idp_subject,
            display_name: p.display_name || undefined,
            title: p.title || undefined,
            photo_url: p.photo_url || undefined,
          }));
        const data = await makeAuthenticatedRequest(async (token) => {
          const response = await axios.post(
            `${API_BASE_URL}/invitations/directory`,
            { org_id: orgId, people },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          return response.data;
        });
        const sent = data?.sent || [];
        const failed = data?.failed || [];
        setFailedRows(failed);
        if (sent.length > 0 && failed.length === 0) {
          toastSuccess(
            sent.length === 1
              ? "Invite sent"
              : `${sent.length} invitations sent`
          );
          resetForm();
          if (onSuccess) onSuccess();
          onClose();
        } else if (sent.length > 0) {
          toastSuccess(
            `${sent.length} sent, ${failed.length} could not be invited`
          );
          setSelectedSubs(
            new Set(
              failed
                .map((row) => {
                  const match = dirPeople.find((p) => p.email === row.email);
                  return match?.idp_subject;
                })
                .filter(Boolean)
            )
          );
          if (onSuccess) onSuccess();
        } else {
          setError(failed[0]?.message || "Could not send invitations");
        }
      } else {
        const { valid, invalid } = partitionInviteEmails(email);
        const localFailed = invalid.map((address) => ({
          email: address,
          message: INVALID_EMAIL_MESSAGE,
        }));
        const data = await makeAuthenticatedRequest(async (token) => {
          const response = await axios.post(
            `${API_BASE_URL}/invitations/batch`,
            { org_id: orgId, emails: valid },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          return response.data;
        });
        const sent = data?.sent || [];
        const failed = [...(data?.failed || []), ...localFailed];
        setFailedRows(failed);
        if (sent.length > 0 && failed.length === 0) {
          toastSuccess(
            sent.length === 1
              ? "Invite sent"
              : `Invited ${sent.length} people`
          );
          resetForm();
          if (onSuccess) onSuccess();
          onClose();
        } else if (sent.length > 0) {
          toastSuccess(
            `Invited ${sent.length}. ${failed.length} could not be sent`
          );
          setEmail(failed.map((row) => row.email).join("\n"));
          if (onSuccess) onSuccess();
        } else {
          setError(failed[0]?.message || "Could not send invitations");
        }
      }
    } catch (err) {
      console.error("Failed to send invitation:", err);
      setError(
        getErrorMessage(err, "Could not send invitations. Try again.")
      );
    } finally {
      setIsInviting(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <DialogContent className="max-w-5xl w-[96vw]">
        <DialogHeader>
          <DialogTitle>
            {isTeamInvite ? "Invite User to Team" : "Invite people"}
          </DialogTitle>
          <DialogDescription>
            {isTeamInvite
              ? "Choose an organization member to invite to this team."
              : "We'll email each work address a link to join Circa and this organization. If SSO is connected, you can also pick people from the directory."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {isTeamInvite ? (
              <>
                <div>
                  <Label htmlFor="team-invite-search" className="sr-only">
                    Search members
                  </Label>
                  <Input
                    id="team-invite-search"
                    type="search"
                    value={memberQuery}
                    onChange={(e) => setMemberQuery(e.target.value)}
                    placeholder="Search by name, org email, or title…"
                    autoFocus
                  />
                </div>
                <div className="max-h-64 overflow-y-auto border rounded-md divide-y">
                  {membersLoading ? (
                    <p className="p-3 text-sm text-muted-foreground">
                      Loading members…
                    </p>
                  ) : filteredCandidates.length === 0 ? (
                    <p className="p-3 text-sm text-muted-foreground">
                      {inviteCandidates.length === 0
                        ? "Everyone in the organization is already on this team."
                        : `No members match “${memberQuery.trim()}”.`}
                    </p>
                  ) : (
                    filteredCandidates.map(({ user: u, title, org_email }) => {
                      const selected = selectedUserId === u.user_id;
                      const contactEmail = org_email || u.org_email;
                      return (
                        <button
                          key={u.user_id}
                          type="button"
                          disabled={isInviting}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/60 disabled:opacity-50 flex items-center gap-3 ${
                            selected ? "bg-primary/5" : ""
                          }`}
                          onClick={() => setSelectedUserId(u.user_id)}
                        >
                          <OrgMemberAvatar
                            user={u}
                            className="h-8 w-8 shrink-0"
                            fallbackClassName="text-[10px]"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="font-medium block truncate">
                              {getFullName(u) || "Unknown"}
                            </span>
                            {[title, contactEmail].filter(Boolean).length > 0 ? (
                              <span className="text-xs text-muted-foreground block truncate">
                                {[title, contactEmail].filter(Boolean).join(" · ")}
                              </span>
                            ) : null}
                          </span>
                          <span className="text-xs text-muted-foreground shrink-0">
                            {selected ? "Selected" : "Select"}
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>
              </>
            ) : (
              <Tabs
                value={directoryEnabled ? inviteTab : "email"}
                onValueChange={setInviteTab}
              >
                {directoryEnabled ? (
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="email">Work emails</TabsTrigger>
                    <TabsTrigger value="directory">From directory</TabsTrigger>
                  </TabsList>
                ) : null}
                {!directoryEnabled && directoryHint ? (
                  <p className="mt-2 text-xs text-muted-foreground">
                    Directory import unavailable: {directoryHint}
                  </p>
                ) : null}
                <TabsContent value="email">
              <div>
                <Label htmlFor="emails">Work emails *</Label>
                <textarea
                  id="emails"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={"name@company.com\nother@company.com"}
                  rows={6}
                  className={cn(
                    "flex min-h-[8rem] w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm mt-2"
                  )}
                />
                <p className="mt-2 text-sm text-muted-foreground">
                  Paste one per line, or separate with commas. They do not need
                  a Circa account yet. Each link asks them to confirm joining as
                  their Circa account.
                </p>
                {failedRows.length > 0 && inviteTab === "email" ? (
                  <ul className="mt-3 space-y-1 text-sm text-destructive">
                    {failedRows.map((row) => (
                      <li key={row.email}>
                        {row.email}: {row.message}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
                </TabsContent>
                <TabsContent value="directory">
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Circa login stays email and password. They get a work-email
                      magic link; first workplace enter hops and binds this IdP
                      person.
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Provider: {dirProvider || "unknown"} · Selecting a group
                      selects all currently discoverable members in that group.
                    </p>
                    <div className="grid gap-3 md:grid-cols-2">
                      <div className="space-y-2 rounded-md border p-2">
                        <Label htmlFor="group-search">Groups</Label>
                        <Input
                          id="group-search"
                          type="search"
                          value={dirGroupQuery}
                          onChange={(e) => setDirGroupQuery(e.target.value)}
                          placeholder="Search groups…"
                        />
                        <div className="max-h-64 overflow-y-auto border rounded-md divide-y">
                          <button
                            type="button"
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/60 ${
                              !dirGroup ? "bg-primary/5" : ""
                            }`}
                            onClick={() => setDirGroup("")}
                          >
                            All users
                          </button>
                          {dirGroups.map((g) => (
                            <button
                              key={g.id}
                              type="button"
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/60 ${
                                dirGroup === g.id ? "bg-primary/5" : ""
                              }`}
                              onClick={() => selectWholeGroup(g.id)}
                              disabled={dirSelectingGroup || isInviting}
                            >
                              {g.name}
                            </button>
                          ))}
                        </div>
                        {dirSelectingGroup ? (
                          <p className="text-xs text-muted-foreground">
                            Selecting entire group…
                          </p>
                        ) : null}
                      </div>
                      <div className="space-y-2 rounded-md border p-2">
                        <Label htmlFor="people-search">Members</Label>
                        <Input
                          id="people-search"
                          type="search"
                          value={dirQuery}
                          onChange={(e) => setDirQuery(e.target.value)}
                          placeholder="Search name, email, or title…"
                        />
                        <div className="max-h-64 overflow-y-auto border rounded-md divide-y">
                          {dirLoading ? (
                            <p className="p-3 text-sm text-muted-foreground">
                              Loading directory…
                            </p>
                          ) : visibleDirPeople.length === 0 ? (
                            <p className="p-3 text-sm text-muted-foreground">
                              No directory people to invite.
                            </p>
                          ) : (
                            visibleDirPeople.map((p) => {
                              const selected = selectedSubs.has(p.idp_subject);
                              return (
                                <button
                                  key={p.idp_subject}
                                  type="button"
                                  disabled={isInviting}
                                  className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/60 disabled:opacity-50 flex items-center gap-3 ${
                                    selected ? "bg-primary/5" : ""
                                  }`}
                                  onClick={() => toggleDirPerson(p.idp_subject)}
                                >
                                  {p.photo_url ? (
                                    <img
                                      src={p.photo_url}
                                      alt=""
                                      className="h-8 w-8 shrink-0 rounded-full object-cover"
                                    />
                                  ) : (
                                    <span className="h-8 w-8 shrink-0 rounded-full bg-muted flex items-center justify-center text-[10px] font-medium">
                                      {(p.display_name || p.email || "?")
                                        .slice(0, 1)
                                        .toUpperCase()}
                                    </span>
                                  )}
                                  <span className="min-w-0 flex-1">
                                    <span className="font-medium block truncate">
                                      {p.display_name || p.email}
                                    </span>
                                    <span className="text-xs text-muted-foreground block truncate">
                                      {[p.title, p.email]
                                        .filter(Boolean)
                                        .join(" · ")}
                                    </span>
                                  </span>
                                  <span className="text-xs text-muted-foreground shrink-0">
                                    {selected ? "Selected" : "Select"}
                                  </span>
                                </button>
                              );
                            })
                          )}
                        </div>
                        {dirPage.hasMore ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={loadMoreDirectoryPeople}
                            disabled={dirLoadingMore || isInviting}
                          >
                            {dirLoadingMore ? "Loading more…" : "Load more"}
                          </Button>
                        ) : null}
                      </div>
                    </div>
                    {selectedSubs.size > 50 ? (
                      <Alert>
                        <AlertDescription>
                          {selectedSubs.size} selected. Only 50 invitations can be
                          sent per submit right now.
                        </AlertDescription>
                      </Alert>
                    ) : null}
                    {failedRows.length > 0 && inviteTab === "directory" ? (
                      <ul className="space-y-1 text-sm text-destructive">
                        {failedRows.map((row) => (
                          <li key={row.email}>
                            {row.email}: {row.message}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                </TabsContent>
              </Tabs>
            )}

            <p className="text-sm text-muted-foreground">
              Invited users join as <span className="font-medium">member</span>.
              {isTeamInvite
                ? " Team admins can change roles afterward."
                : " Organization admins can promote members afterward."}
            </p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isInviting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                isInviting ||
                (isTeamInvite && (!selectedUserId || membersLoading)) ||
                (!isTeamInvite &&
                  inviteTab === "directory" &&
                  selectedSubs.size === 0)
              }
            >
              {isInviting
                ? "Sending..."
                : isTeamInvite
                  ? "Send Invitation"
                  : inviteTab === "directory"
                    ? selectedSubs.size
                      ? `Send ${selectedSubs.size} invitation${
                          selectedSubs.size === 1 ? "" : "s"
                        }`
                      : "Send invitations"
                    : "Send invitations"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default InviteUserDialog;
